/**
 * AudioPlayer — abstraction for audio operations.
 * Decouples useRetreatHandlers from the concrete audioPlane singleton.
 */

import type { BellInstrumentId, BellPatternId } from "@retreat/core";

export interface AudioPlayer {
  playPreviewBell(): void;
  /** Play a single strike of a specific instrument (Bell Library preview). */
  playInstrumentPreview(instrumentId: BellInstrumentId): void;
  /** Play a full named bell pattern (used by retreat signal previews). */
  playPattern(patternId: BellPatternId): void;
  resume(): Promise<void>;
  setTradition(traditionId: string): void;
  clearTradition(): void;
}

export function createAudioPlayerAdapter(
  plane: typeof import("../planes").audioPlane,
): AudioPlayer {
  return {
    playPreviewBell: () => plane.playPreviewBell(),
    playInstrumentPreview: (id) => plane.playInstrumentPreview?.(id),
    playPattern: (id) => plane.playPattern?.(id),
    resume: () => plane.resume?.() ?? Promise.resolve(),
    setTradition: (id) => plane.setTradition?.(id as any),
    clearTradition: () => plane.clearTradition?.(),
  };
}

export function createMockAudioPlayer(): AudioPlayer & { calls: string[] } {
  const calls: string[] = [];
  return {
    playPreviewBell: () => calls.push("playPreviewBell"),
    playInstrumentPreview: (id) => calls.push(`playInstrumentPreview:${id}`),
    playPattern: (id) => calls.push(`playPattern:${id}`),
    resume: async () => { calls.push("resume"); },
    setTradition: (id) => calls.push(`setTradition:${id}`),
    clearTradition: () => calls.push("clearTradition"),
    calls,
  };
}
